/**
 * Agent usage — the signed-in agent's own AI activity for the current billing
 * period: call/talk-time/document counts + a recent-activity feed. Reads
 * GET /profile/me/usage (mocked in dev until the real endpoint ships).
 */
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { useRoles } from "../lib/roles";
import "./MyUsage.css";

interface UsageData {
  calls: { total: number; ai_answered: number; transferred: number };
  talk_time: { total_min: number; avg_min_per_call: number };
  docs: { total: number; auto_classified: number; manual: number };
  recent: {
    id: string;
    kind: "call" | "doc" | "reply";
    title: string;
    detail: string;
    when: string;
  }[];
}

// ---- inline line icons (admin has no icon lib) ------------------------------

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}
const PhoneIcon = () => (
  <Svg>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </Svg>
);
const MicIcon = () => (
  <Svg>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </Svg>
);
const FileIcon = () => (
  <Svg>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </Svg>
);
const ChatIcon = () => (
  <Svg>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </Svg>
);

const KIND_ICON: Record<UsageData["recent"][number]["kind"], ReactNode> = {
  call: <PhoneIcon />,
  doc: <FileIcon />,
  reply: <ChatIcon />,
};

// ---- pieces -----------------------------------------------------------------

function Stat({
  icon,
  label,
  value,
  sub,
  positive,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub: string;
  positive?: boolean;
}) {
  return (
    <div className="rp-card rp-card--pad-md">
      <div className="usg-stat__head">
        <span className="usg-stat__icon">{icon}</span>
        {label}
      </div>
      <div className="usg-stat__num">{value}</div>
      <div className={`usg-stat__sub ${positive ? "usg-stat__sub--pos" : ""}`}>
        {sub}
      </div>
    </div>
  );
}

export function MyUsage() {
  const { primary } = useRoles();
  const isAgent = primary === "agent";
  const q = useQuery({
    queryKey: ["my-usage"],
    queryFn: () => api.request<UsageData>("/profile/me/usage"),
  });
  const d = q.data;
  const dash = "—";

  return (
    <div className="usg">
      <h1 className="usg__title">{isAgent ? "My usage" : "Agent usage"}</h1>
      <p className="usg__subtitle">
        {isAgent ? "Your" : "Your agents'"} AI activity this billing period —
        calls, talk time, and documents processed.
      </p>

      <div className="usg-stats">
        <Stat
          icon={<PhoneIcon />}
          label="AI calls"
          value={d?.calls.total ?? dash}
          positive
          sub={
            d
              ? `${d.calls.ai_answered} answered by AI · ${d.calls.transferred} transferred`
              : ""
          }
        />
        <Stat
          icon={<MicIcon />}
          label="Talk time"
          value={d ? `${d.talk_time.total_min} min` : dash}
          sub={d ? `Avg ${d.talk_time.avg_min_per_call} min / call` : ""}
        />
        <Stat
          icon={<FileIcon />}
          label="Docs processed"
          value={d?.docs.total ?? dash}
          sub={
            d
              ? `${d.docs.auto_classified} auto-classified · ${d.docs.manual} manual`
              : ""
          }
        />
      </div>

      <section className="rp-card rp-card--pad-md">
        <div className="usg-activity__title">Recent AI activity</div>
        {(d?.recent ?? []).map((r) => (
          <div className="usg-row" key={r.id}>
            <div className="usg-row__icon">{KIND_ICON[r.kind]}</div>
            <div className="usg-row__body">
              <div className="usg-row__title">{r.title}</div>
              <div className="usg-row__detail">{r.detail}</div>
            </div>
            <div className="usg-row__when">{r.when}</div>
          </div>
        ))}
        {d && d.recent.length === 0 && (
          <p
            style={{
              color: "var(--rp-color-text-muted)",
              fontSize: 13.5,
              margin: "12px 0 0",
            }}
          >
            No AI activity yet this period.
          </p>
        )}
      </section>
    </div>
  );
}
